import React from "react";
import axios from "axios";
import { authentication, app } from "@microsoft/teams-js";
import type { Message } from "../types";
const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || "";

const getChatbotResponse = (userMessage: string): string | null => {
  const lowerMessage = userMessage.toLowerCase().trim();

  if (lowerMessage.match(/^(hi|hello|hey|greetings)$/i)) {
    return "Hello! 👋 How can I assist you today?";
  }

  if (lowerMessage.match(/^(help|what can you do|commands)$/i)) {
    return "I can help you with various tasks! Try asking me about:\n• General information\n• Product features\n• Account details\n• Or anything else you need!";
  }

  if (lowerMessage.match(/^(how are you|how's it going|what's up)$/i)) {
    return "I'm doing great! Thanks for asking. How can I help you? 😊";
  }

  if (lowerMessage.match(/^(thanks|thank you|appreciate it)$/i)) {
    return "You're welcome! Happy to help. Is there anything else? 😊";
  }

  if (lowerMessage.match(/^(bye|goodbye|see you|farewell)$/i)) {
    return "Goodbye! Thanks for chatting with me. Have a great day! 👋";
  }

  if (lowerMessage.match(/^(what time|current time|time now)$/i)) {
    return `Current time: ${new Date().toLocaleTimeString()}`;
  }

  if (lowerMessage.match(/^(what date|current date|today)$/i)) {
    return `Today's date: ${new Date().toLocaleDateString()}`;
  }

  return null;
};

export const useChat = (
  getAccessToken: () => Promise<string>,
  userFullName: string = "",
) => {
  const axiosInstance = axios.create();

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        try {
          await app.initialize();
          const newToken = await authentication.getAuthToken();
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(error.config);
        } catch (tokenError) {
          return Promise.reject(tokenError);
        }
      }
      return Promise.reject(error);
    },
  );

  const getInitialMessage = (name: string) => {
    if (!name) return "Hello! How can I help you today?";
    const firstName = name.split(" ")[0];
    const capitalizedName =
      firstName.charAt(0).toUpperCase() + firstName.slice(1);
    return `Hello ${capitalizedName}! How can I help you today?`;
  };

  const [messages, setMessages] = React.useState<Message[]>([
    {
      id: 1,
      text: getInitialMessage(userFullName),
      sender: "bot",
      timestamp: new Date(),
    },
  ]);

  const [isWaitingForResponse, setIsWaitingForResponse] = React.useState(false);

  // Update initial message when userFullName changes
  React.useEffect(() => {
    if (userFullName) {
      setMessages((prev) => {
        if (prev.length > 0 && prev[0].id === 1) {
          const updatedMessages = [...prev];
          updatedMessages[0] = {
            ...updatedMessages[0],
            text: getInitialMessage(userFullName),
          };
          return updatedMessages;
        }
        return prev;
      });
    }
  }, [userFullName]);

  const sendMessage = async (messageText: string) => {
    if (messageText.trim() === "" || isWaitingForResponse) return;

    const newMessage: Message = {
      id: Date.now(),
      text: messageText,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setIsWaitingForResponse(true);

    const chatbotResponse = getChatbotResponse(messageText);

    if (chatbotResponse) {
      console.log("Generic message detected, responding locally");

      const botMessage: Message = {
        id: Date.now() + 1,
        text: chatbotResponse,
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsWaitingForResponse(false);
    } else {
      // Add loading message
      const loadingMessageId = Date.now() + 1;
      const loadingMessage: Message = {
        id: loadingMessageId,
        text: "Generating response...",
        sender: "bot",
        timestamp: new Date(),
        isGenerating: true,
      };
      setMessages((prev) => [...prev, loadingMessage]);

      try {
        const accessToken = await getAccessToken();

        const response = await axiosInstance.post(
          `${apiEndpoint}/api/messages`,
          {
            text: newMessage.text,
            sender: "user",
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: 'arraybuffer',
          },
        );

        // 🔍 Detect response type
        const contentType = response.headers["content-type"] || "";
        console.log("Content-Type:", contentType);
        let botMessage: Message;

        // ===============================
        // 📄 FILE RESPONSE
        // ===============================
        if (!contentType.includes("application/json")) {
          console.log("File response detected");
          const rawData = response.data;
          const blob = new Blob([rawData], {
            type: contentType || "application/octet-stream",
          });

          if (blob.size === 0) {
            throw new Error("Received an invalid file response from the server");
          }

          // Try to extract filename and extension
          const uuid = crypto.randomUUID();
          let filename = `TPlay_hrbot_file_${uuid}`;
          let ext = "";

          const disposition = response.headers["content-disposition"];

          if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match) {
              const originalName = match[1];
              ext = originalName.includes(".")
                ? originalName.substring(originalName.lastIndexOf("."))
                : "";
            }
          }

          // If no extension found, try to get from content-type
          if (!ext) {
            const mimeType = blob.type || contentType;
            const mimeToExt: { [key: string]: string } = {
              "application/pdf": ".pdf",
              "application/msword": ".doc",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                ".docx",
              "application/vnd.ms-excel": ".xls",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                ".xlsx",
              "text/plain": ".txt",
              "text/csv": ".csv",
              "image/png": ".png",
              "image/jpeg": ".jpg",
              "image/gif": ".gif",
            };
            ext = mimeToExt[mimeType] || ".bin";
          }

          filename = `${filename}${ext}`;
          console.log("Filename:", filename);

          const url = window.URL.createObjectURL(blob);
          console.log("URL:", url);

          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();

          a.remove();
          window.URL.revokeObjectURL(url);

          botMessage = {
            id: Date.now() + 1,
            text: `File "${filename}" downloaded successfully`,
            sender: "bot",
            timestamp: new Date(),
          };
        } else {
          // ===============================
          // 📝 TEXT RESPONSE
          // ===============================
          const text = new TextDecoder().decode(response.data);
          const data = JSON.parse(text);
          console.log("API response:", data);

          const responseText =
            data.final_output ||
            data.response ||
            data.message ||
            data.text ||
            "No response";
          botMessage = {
            id: Date.now() + 1,
            text: responseText,
            sender: "bot",
            timestamp: new Date(),
          };
        }

        // Remove loading message and add actual response
        setMessages((prev) => [
          ...prev.filter((msg) => msg.id !== loadingMessageId),
          botMessage,
        ]);
        setIsWaitingForResponse(false);
      } catch (error) {
        // Remove loading message on error
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== loadingMessageId),
        );
        console.error("Error sending message to backend:", error);

        const errorMessage: Message = {
          id: Date.now() + 1,
          text: `Failed to send message: ${error instanceof Error ? error.message : String(error)}`,
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsWaitingForResponse(false);
      }
    }
  };

  return {
    messages,
    sendMessage,
    setMessages,
    isWaitingForResponse,
  };
};
